import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { SectionCard } from '@/components/SectionCard';
import { Skeleton } from '@/components/States';
import { fetchTenantAiCredits, grantAiCredits, tenantKeys } from '@/features/tenants/api';
import { getErrorMessage } from '@/lib/api';

/**
 * Ask Pilot for one tenant: what its plan gives this month, and boost credits
 * the SA adds when a tenant buys a pack (or to let a Starter tenant try it).
 */
export function AiCreditsSection({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState('100');
  const [note, setNote] = useState('');
  const balance = useQuery({
    queryKey: tenantKeys.aiCredits(tenantId),
    queryFn: ({ signal }) => fetchTenantAiCredits(tenantId, signal),
  });
  const grant = useMutation({
    mutationFn: () => grantAiCredits(tenantId, { amount: Number(amount), note: note.trim() }),
    onSuccess: () => {
      setNote('');
      void queryClient.invalidateQueries({ queryKey: tenantKeys.aiCredits(tenantId) });
    },
  });
  const c = balance.data;
  const valid = Number.isInteger(Number(amount)) && Number(amount) >= 1 && Number(amount) <= 10000 && note.trim().length > 0;

  return (
    <SectionCard
      headingId="tenant-ask-pilot"
      title="Ask Pilot"
      description="AI reports. The plan sets the monthly credits; boosts add more and never expire."
    >
      <div className="space-y-4 px-5 py-4">
        {balance.isLoading ? (
          <Skeleton className="h-4 w-64" />
        ) : balance.isError || !c ? (
          <Alert tone="error">{getErrorMessage(balance.error, 'Could not load AI credits.')}</Alert>
        ) : (
          <div className="grid gap-3 text-sm sm:grid-cols-3">
            <div>
              <p className="text-xs text-slate-500">This month ({c.plan ?? 'no plan'})</p>
              <p className="font-medium text-slate-900">
                {c.allowance > 0 ? `${Math.max(c.allowance - c.usedThisMonth, 0)} of ${c.allowance} left` : 'Not on plan'}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Boost</p>
              <p className="font-medium text-slate-900">
                {c.boostGranted > 0 ? `${c.boost} of ${c.boostGranted} left` : 'None'}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Status</p>
              <p className="font-medium text-slate-900">
                {!c.configured ? 'Server has no API key' : c.enabled ? 'On' : 'Off — upgrade or add a boost'}
              </p>
            </div>
          </div>
        )}

        <form
          className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) grant.mutate();
          }}
        >
          <label className="block w-28">
            <span className="mb-1 block text-xs font-medium text-slate-500">Add credits</span>
            <input type="number" min={1} max={10000} className="input" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </label>
          <label className="block min-w-[14rem] flex-1">
            <span className="mb-1 block text-xs font-medium text-slate-500">Note (why)</span>
            <input
              type="text"
              maxLength={200}
              className="input"
              placeholder="e.g. Boost 100, paid UPI 23 Oct"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          <Button type="submit" size="sm" variant="primary" loading={grant.isPending} disabled={!valid}>
            Add boost
          </Button>
        </form>
        {grant.isError ? <Alert tone="error">{getErrorMessage(grant.error)}</Alert> : null}
      </div>
    </SectionCard>
  );
}
